import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, Settings, Eye, Copy, ChevronDown, ChevronUp, Palette } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input', icon: '📝' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'textarea', label: 'Text Area', icon: '📄' },
  { value: 'number', label: 'Number', icon: '🔢' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'radio', label: 'Radio Buttons', icon: '🔘' },
  { value: 'checkbox', label: 'Checkboxes', icon: '☑️' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'phone', label: 'Phone', icon: '📞' },
  { value: 'recipient_select', label: 'Recipient Dropdown', icon: '👤' },
];

// Separate component for options textarea with local state management
const OptionsTextarea = ({ fieldId, options, onUpdate }) => {
  const [text, setText] = useState((options || []).join('\n'));
  
  const handleBlur = () => {
    const newOptions = text.split('\n').filter(o => o.trim());
    onUpdate(fieldId, { options: newOptions });
  };
  
  return (
    <div>
      <Label>Options (one per line)</Label>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        placeholder={"Option 1\nOption 2\nOption 3"}
        className="mt-1"
        rows={4}
        data-testid={`field-options-${fieldId}`}
      />
    </div>
  );
};

const SortableField = ({ field, index, onUpdate, onDelete, isExpanded, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.field_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldType = FIELD_TYPES.find(t => t.value === field.type) || FIELD_TYPES[0];

  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-slate-200 rounded-lg mb-3">
      <Collapsible open={isExpanded} onOpenChange={onToggle}>
        <div className="flex items-center gap-2 p-3 border-b border-slate-100">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded">
            <GripVertical size={16} className="text-slate-400" />
          </div>
          <span className="text-lg">{fieldType.icon}</span>
          <div className="flex-1">
            <span className="font-medium text-slate-900">{field.label || 'Untitled Field'}</span>
            <Badge variant="outline" className="ml-2 text-xs">{fieldType.label}</Badge>
            {field.required && <Badge className="ml-2 text-xs bg-red-100 text-red-700">Required</Badge>}
          </div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </CollapsibleTrigger>
          <Button variant="ghost" size="sm" onClick={() => onDelete(field.field_id)} className="text-red-500 hover:text-red-700">
            <Trash2 size={16} />
          </Button>
        </div>
        <CollapsibleContent>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Field Label *</Label>
                <Input
                  value={field.label}
                  onChange={(e) => onUpdate(field.field_id, { label: e.target.value })}
                  placeholder="Enter field label"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Field Type</Label>
                <Select value={field.type} onValueChange={(v) => onUpdate(field.field_id, { type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="mr-2">{type.icon}</span> {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Placeholder Text</Label>
              <Input
                value={field.placeholder || ''}
                onChange={(e) => onUpdate(field.field_id, { placeholder: e.target.value })}
                placeholder="Enter placeholder text"
                className="mt-1"
              />
            </div>
            {['select', 'radio', 'checkbox'].includes(field.type) && (
              <OptionsTextarea
                fieldId={field.field_id}
                options={field.options}
                onUpdate={onUpdate}
              />
            )}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={field.required}
                  onCheckedChange={(checked) => onUpdate(field.field_id, { required: checked })}
                />
                <Label>Required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label>Width:</Label>
                <Select value={field.width || 'full'} onValueChange={(v) => onUpdate(field.field_id, { width: v })}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Width</SelectItem>
                    <SelectItem value="half">Half Width</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const FormEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedFields, setExpandedFields] = useState(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [subscriberGroups, setSubscriberGroups] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchForm();
    fetchSubscriberGroups();
  }, [id]);

  const fetchSubscriberGroups = async () => {
    try {
      const res = await api.get('/subscriber-groups');
      setSubscriberGroups(res.data || []);
    } catch (e) { /* non-fatal */ }
  };

  const fetchForm = async () => {
    try {
      const response = await api.get(`/forms/${id}`);
      setForm(response.data);
      // Expand first field by default if it exists
      if (response.data.fields?.length > 0) {
        setExpandedFields(new Set([response.data.fields[0].field_id]));
      }
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/forms/${id}`, form);
      toast.success('Form saved successfully');
    } catch (error) {
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const addField = (type = 'text') => {
    const fieldId = `field_${Date.now()}`;
    const newField = {
      field_id: fieldId,
      type,
      label: '',
      placeholder: '',
      required: false,
      options: [],
      order: form.fields.length,
      width: 'full'
    };
    setForm({ ...form, fields: [...form.fields, newField] });
    setExpandedFields(new Set([...expandedFields, fieldId]));
  };

  const updateField = (fieldId, updates) => {
    setForm({
      ...form,
      fields: form.fields.map(f => f.field_id === fieldId ? { ...f, ...updates } : f)
    });
  };

  const deleteField = (fieldId) => {
    setForm({
      ...form,
      fields: form.fields.filter(f => f.field_id !== fieldId)
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = form.fields.findIndex(f => f.field_id === active.id);
      const newIndex = form.fields.findIndex(f => f.field_id === over.id);
      setForm({
        ...form,
        fields: arrayMove(form.fields, oldIndex, newIndex).map((f, i) => ({ ...f, order: i }))
      });
    }
  };

  const toggleFieldExpand = (fieldId) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldId)) {
      newExpanded.delete(fieldId);
    } else {
      newExpanded.add(fieldId);
    }
    setExpandedFields(newExpanded);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Loading form...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/forms')}>
              <ArrowLeft size={18} className="mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{form.name}</h1>
              <p className="text-sm text-slate-500">{form.fields.length} fields · {form.submission_count} submissions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye size={18} className="mr-2" /> {showPreview ? 'Edit' : 'Preview'}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/forms/${id}/submissions`)}>
              View Submissions
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              <Save size={18} className="mr-2" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Form Fields */}
          <div className="col-span-2">
            {showPreview ? (
              <Card>
                <CardHeader>
                  <CardTitle>{form.name}</CardTitle>
                  {form.description && <p className="text-slate-500">{form.description}</p>}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {form.fields.map((field) => (
                      <div key={field.field_id} className={field.width === 'half' ? 'w-1/2 inline-block pr-2' : 'w-full'}>
                        <Label className="mb-1 block">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        {field.type === 'textarea' ? (
                          <Textarea placeholder={field.placeholder} disabled />
                        ) : field.type === 'select' ? (
                          <Select disabled>
                            <SelectTrigger>
                              <SelectValue placeholder={field.placeholder || 'Select...'} />
                            </SelectTrigger>
                          </Select>
                        ) : field.type === 'recipient_select' ? (
                          <Select disabled>
                            <SelectTrigger>
                              <SelectValue placeholder={field.placeholder || 'Select recipient...'} />
                            </SelectTrigger>
                            <SelectContent>
                              {(form.recipients || []).map((r, i) => (
                                <SelectItem key={i} value={r.name}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : field.type === 'radio' ? (
                          <div className="space-y-2">
                            {(field.options || []).map((opt, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <input type="radio" disabled /> <span>{opt}</span>
                              </div>
                            ))}
                          </div>
                        ) : field.type === 'checkbox' ? (
                          <div className="space-y-2">
                            {(field.options || []).map((opt, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <input type="checkbox" disabled /> <span>{opt}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Input type={field.type} placeholder={field.placeholder} disabled />
                        )}
                      </div>
                    ))}
                    <Button className="mt-4" disabled>{form.submit_button_text}</Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="mb-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Form Fields</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {form.fields.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <p className="mb-4">No fields yet. Add your first field to get started.</p>
                      </div>
                    ) : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={form.fields.map(f => f.field_id)} strategy={verticalListSortingStrategy}>
                          {form.fields.map((field, index) => (
                            <SortableField
                              key={field.field_id}
                              field={field}
                              index={index}
                              onUpdate={updateField}
                              onDelete={deleteField}
                              isExpanded={expandedFields.has(field.field_id)}
                              onToggle={() => toggleFieldExpand(field.field_id)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    )}
                  </CardContent>
                </Card>

                {/* Add Field Buttons */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Add Field</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-2">
                      {FIELD_TYPES.map(type => (
                        <Button
                          key={type.value}
                          variant="outline"
                          className="justify-start"
                          onClick={() => addField(type.value)}
                        >
                          <span className="mr-2">{type.icon}</span> {type.label}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Settings Panel */}
          <div className="col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings size={18} /> Form Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Internal Name <span className="text-slate-400 font-normal">(admin only)</span></Label>
                  <Input
                    value={form.internal_name || ''}
                    onChange={(e) => setForm({ ...form, internal_name: e.target.value })}
                    className="mt-1"
                    placeholder="e.g. Contact Form — Homepage v2"
                    data-testid="form-internal-name-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Shown in the admin Forms list. Never displayed on the public form.</p>
                </div>
                <div>
                  <Label>Form Name <span className="text-slate-400 font-normal">(public)</span></Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1"
                    data-testid="form-public-name-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Submit Button Text</Label>
                  <Input
                    value={form.submit_button_text}
                    onChange={(e) => setForm({ ...form, submit_button_text: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Success Message</Label>
                  <Textarea
                    value={form.success_message}
                    onChange={(e) => setForm({ ...form, success_message: e.target.value })}
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Notification Email (optional)</Label>
                  <Input
                    type="email"
                    value={form.notify_email || ''}
                    onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
                    placeholder="Receive email on submission"
                    className="mt-1"
                  />
                </div>
                
                {/* Recipients List for Dropdown */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Recipient Dropdown Options</Label>
                  <p className="text-sm text-slate-500 mb-3">Add people who can receive form submissions. Users can select from this list when submitting the form.</p>
                  
                  {(form.recipients || []).map((recipient, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        value={recipient.name}
                        onChange={(e) => {
                          const newRecipients = [...(form.recipients || [])];
                          newRecipients[index] = { ...newRecipients[index], name: e.target.value };
                          setForm({ ...form, recipients: newRecipients });
                        }}
                        placeholder="Name (shown to user)"
                        className="flex-1"
                      />
                      <Input
                        type="email"
                        value={recipient.email}
                        onChange={(e) => {
                          const newRecipients = [...(form.recipients || [])];
                          newRecipients[index] = { ...newRecipients[index], email: e.target.value };
                          setForm({ ...form, recipients: newRecipients });
                        }}
                        placeholder="Email address"
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newRecipients = (form.recipients || []).filter((_, i) => i !== index);
                          setForm({ ...form, recipients: newRecipients });
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newRecipients = [...(form.recipients || []), { name: '', email: '' }];
                      setForm({ ...form, recipients: newRecipients });
                    }}
                    className="mt-2"
                  >
                    <Plus size={16} className="mr-1" /> Add Recipient
                  </Button>
                  
                  {(form.recipients || []).length > 0 && (
                    <p className="text-xs text-slate-400 mt-2">
                      Add a "Recipient Dropdown" field to let users select who to send the form to.
                    </p>
                  )}
                </div>
                
                {/* Newsletter Subscribers List */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">Add to Newsletter Subscribers</Label>
                      <p className="text-xs text-slate-400 mt-1 max-w-xs">
                        When someone submits this form, add them to your newsletter subscriber list.
                      </p>
                    </div>
                    <Switch
                      checked={!!form.add_to_subscriber_groups}
                      onCheckedChange={(checked) => setForm({ ...form, add_to_subscriber_groups: checked })}
                      data-testid="form-add-subscriber-toggle"
                    />
                  </div>

                  {form.add_to_subscriber_groups && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <Label className="text-sm">Add subscribers to these list(s)</Label>
                        {subscriberGroups.length === 0 ? (
                          <p className="text-xs text-amber-600 mt-1">
                            No subscriber lists yet. Create one under <strong>Newsletter → Subscribers</strong> first, then come back.
                          </p>
                        ) : (
                          <div className="mt-1 space-y-1.5 max-h-40 overflow-y-auto border rounded-lg p-2">
                            {subscriberGroups.map((g) => {
                              const gid = g.group_id;
                              const selected = (form.subscriber_group_ids || []).includes(gid);
                              return (
                                <label key={gid} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`form-group-option-${gid}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => {
                                      const cur = new Set(form.subscriber_group_ids || []);
                                      if (e.target.checked) cur.add(gid); else cur.delete(gid);
                                      setForm({ ...form, subscriber_group_ids: Array.from(cur) });
                                    }}
                                  />
                                  <span>{g.name}</span>
                                  {typeof g.subscriber_count === 'number' && (
                                    <span className="text-xs text-slate-400">({g.subscriber_count})</span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div>
                        <Label className="text-sm">Which field holds the email address?</Label>
                        <select
                          value={form.email_field_id || ''}
                          onChange={(e) => setForm({ ...form, email_field_id: e.target.value || null })}
                          className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                          data-testid="form-email-field-select"
                        >
                          <option value="">Auto-detect (first Email field)</option>
                          {(form.fields || []).map((f) => (
                            <option key={f.field_id} value={f.field_id}>
                              {f.label || f.field_id}{f.type === 'email' ? ' (email)' : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-400 mt-1">
                          Tip: for reliable detection, add an <strong>Email</strong> type field to this form.
                        </p>
                      </div>

                      <div>
                        <Label className="text-sm">Which field holds their name? (optional)</Label>
                        <select
                          value={form.name_field_id || ''}
                          onChange={(e) => setForm({ ...form, name_field_id: e.target.value || null })}
                          className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                          data-testid="form-name-field-select"
                        >
                          <option value="">Auto-detect</option>
                          {(form.fields || []).map((f) => (
                            <option key={f.field_id} value={f.field_id}>{f.label || f.field_id}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Label>Form Status</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={form.status === 'active'}
                      onCheckedChange={(checked) => setForm({ ...form, status: checked ? 'active' : 'inactive' })}
                    />
                    <span className="text-sm text-slate-600">{form.status === 'active' ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t pt-4">
                  <div>
                    <Label>Ask for preferred language</Label>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs">
                      Shows a language dropdown so subscribers choose which language to receive newsletters in.
                    </p>
                  </div>
                  <Switch
                    checked={!!form.collect_language_preference}
                    onCheckedChange={(checked) => setForm({ ...form, collect_language_preference: checked })}
                    data-testid="form-collect-language-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Embed Code */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Copy size={18} /> Embed Form
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-2">Use this form in your pages by adding a Form block in the page editor and selecting this form.</p>
                <div className="bg-slate-100 p-2 rounded text-xs font-mono">
                  Form ID: {form.form_id}
                </div>
              </CardContent>
            </Card>

            {/* Styling */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette size={18} /> Form Styling
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Use Site Theme</Label>
                  <Switch
                    checked={form.styles?.use_site_theme !== false}
                    onCheckedChange={(checked) => setForm({ 
                      ...form, 
                      styles: { ...getDefaultStyles(), ...form.styles, use_site_theme: checked }
                    })}
                  />
                </div>
                
                {form.styles?.use_site_theme === false && (
                  <div className="space-y-4 pt-2 border-t border-slate-100">
                    {/* Layout Controls */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 mb-2 block">Layout</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Form Layout</Label>
                          <Select 
                            value={form.styles?.layout || 'stacked'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, layout: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="stacked">Stacked (1 Column)</SelectItem>
                              <SelectItem value="two-column">2 Columns</SelectItem>
                              <SelectItem value="three-column">3 Columns</SelectItem>
                              <SelectItem value="inline">Inline</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Form Width</Label>
                          <Select 
                            value={form.styles?.formWidth || '100'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, formWidth: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="50">50%</SelectItem>
                              <SelectItem value="60">60%</SelectItem>
                              <SelectItem value="70">70%</SelectItem>
                              <SelectItem value="80">80%</SelectItem>
                              <SelectItem value="90">90%</SelectItem>
                              <SelectItem value="100">100%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Field Styling */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 mb-2 block">Fields</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Size</Label>
                          <Select 
                            value={form.styles?.fieldSize || 'md'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, fieldSize: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="lg">Large</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Corners</Label>
                          <Select 
                            value={form.styles?.fieldRadius || 'rounded'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, fieldRadius: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Square</SelectItem>
                              <SelectItem value="rounded">Rounded</SelectItem>
                              <SelectItem value="pill">Pill</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Spacing</Label>
                          <Select 
                            value={form.styles?.fieldGap || '16'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, fieldGap: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="8">Compact</SelectItem>
                              <SelectItem value="16">Normal</SelectItem>
                              <SelectItem value="24">Spacious</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Button Styling */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 mb-2 block">Button</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Width</Label>
                          <Select 
                            value={form.styles?.buttonWidth || 'full'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, buttonWidth: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto</SelectItem>
                              <SelectItem value="50">50%</SelectItem>
                              <SelectItem value="75">75%</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Style</Label>
                          <Select 
                            value={form.styles?.buttonStyle || 'solid'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, buttonStyle: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solid">Solid</SelectItem>
                              <SelectItem value="outline">Outline</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Align</Label>
                          <Select 
                            value={form.styles?.buttonAlign || 'left'} 
                            onValueChange={(v) => setForm({
                              ...form,
                              styles: { ...form.styles, buttonAlign: v }
                            })}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Colors */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 mb-2 block">Colors</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Background</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={form.styles?.background_color || '#ffffff'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, background_color: e.target.value }
                              })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                            <Input
                              value={form.styles?.background_color || '#ffffff'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, background_color: e.target.value }
                              })}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Label Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={form.styles?.label_color || '#374151'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, label_color: e.target.value }
                              })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                            <Input
                              value={form.styles?.label_color || '#374151'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, label_color: e.target.value }
                              })}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <Label className="text-xs text-slate-500">Button Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={form.styles?.button_color || '#4f46e5'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, button_color: e.target.value }
                              })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                            <Input
                              value={form.styles?.button_color || '#4f46e5'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, button_color: e.target.value }
                              })}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Button Text</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={form.styles?.button_text_color || '#ffffff'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, button_text_color: e.target.value }
                              })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                            <Input
                              value={form.styles?.button_text_color || '#ffffff'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, button_text_color: e.target.value }
                              })}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <Label className="text-xs text-slate-500">Input Border</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={form.styles?.input_border_color || '#d1d5db'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, input_border_color: e.target.value }
                              })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                            <Input
                              value={form.styles?.input_border_color || '#d1d5db'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, input_border_color: e.target.value }
                              })}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Text Color</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={form.styles?.text_color || '#1e293b'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, text_color: e.target.value }
                              })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                            <Input
                              value={form.styles?.text_color || '#1e293b'}
                              onChange={(e) => setForm({
                                ...form,
                                styles: { ...form.styles, text_color: e.target.value }
                              })}
                              className="flex-1 text-xs h-8"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const getDefaultStyles = () => ({
  use_site_theme: true,
  background_color: '#ffffff',
  text_color: '#1e293b',
  label_color: '#374151',
  input_background: '#ffffff',
  input_border_color: '#d1d5db',
  input_text_color: '#1e293b',
  button_color: '#4f46e5',
  button_text_color: '#ffffff',
  button_hover_color: '#4338ca',
  border_radius: '8px',
  padding: '24px',
  font_family: 'inherit'
});

export default FormEditor;
